/**
 * Offline Sync Service
 * Handles syncing offline sales with the server
 */

import axios from 'axios';
import {
  getPendingOfflineSales,
  markSaleAsSynced,
  updateSyncAttempt,
  clearSyncedSales,
  setLastSyncTime,
  cacheProducts,
  cacheCustomers,
} from './offlineStorage';

const API = process.env.REACT_APP_BACKEND_URL;

// Sync status
let isSyncing = false;
let syncListeners = [];

/**
 * Register a listener for sync status updates
 */
export const onSyncStatusChange = (callback) => {
  syncListeners.push(callback);
  return () => {
    syncListeners = syncListeners.filter(cb => cb !== callback);
  };
};

/**
 * Notify all listeners of sync status change
 */
const notifySyncStatus = (status) => {
  syncListeners.forEach(cb => cb(status));
};

/**
 * Sync a single offline sale to the server
 */
const syncSingleSale = async (sale) => {
  try {
    // Prepare sale data for server - matching the expected SaleTransactionCreate model
    const saleData = {
      customer_id: sale.customer_id || null,
      customer_name: sale.customer_name || null,
      customer_account_number: sale.customer_account_number || null,
      customer_address: sale.customer_address || null,
      customer_suburb: sale.customer_suburb || null,
      customer_province: sale.customer_province || null,
      customer_contact: sale.customer_contact || null,
      customer_vat_number: sale.customer_vat_number || null,
      customer_email: sale.customer_email || null,
      customer_order_number: sale.customer_order_number || null,
      transaction_type: sale.transaction_type || "CASH SALE",
      items: sale.items || [],
      subtotal: sale.subtotal || 0,
      discount: sale.discount || 0,
      customer_discount: sale.customer_discount || 0,
      customer_discount_percent: sale.customer_discount_percent || 0,
      tax: sale.tax || sale.vat_amount || 0,
      total: sale.total || 0,
      payments: sale.payments || [{ method: sale.payment_method || "CASH", amount: sale.total || 0 }],
      cashier: sale.cashier || "POS",
      price_level: sale.price_level || 1,
      note: sale.note || null,
    };

    console.log('Syncing offline sale:', saleData);

    // Retry up to 3 times with increasing delay
    let lastError = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await axios.post(`${API}/api/sales/transaction`, saleData, {
          timeout: 30000, // 30 second timeout for sale sync
        });
        
        if (response.data && response.data.id) {
          await markSaleAsSynced(sale.offline_id, response.data.id);
          return { success: true, serverId: response.data.id };
        } else {
          throw new Error('Invalid server response');
        }
      } catch (err) {
        lastError = err;
        // Don't retry on validation errors (400/422) - they'll always fail
        if (err.response && err.response.status >= 400 && err.response.status < 500) {
          break;
        }
        // Wait before retry (2s, 4s)
        if (attempt < 3) {
          await new Promise(r => setTimeout(r, attempt * 2000));
        }
      }
    }
    
    throw lastError || new Error('Sync failed after retries');
  } catch (error) {
    console.error('Sync error for sale:', sale.offline_id, error);
    const errorMessage = error.response?.data?.detail || error.message;
    await updateSyncAttempt(sale.offline_id, errorMessage);
    return { success: false, error: errorMessage };
  }
};

/**
 * Sync all pending offline sales
 */
export const syncOfflineSales = async (options = {}) => {
  const { onProgress, autoMode = false } = options;

  if (isSyncing) {
    console.log('Sync already in progress');
    return { success: false, message: 'Sync already in progress' };
  }

  // Check if online
  if (!navigator.onLine) {
    return { success: false, message: 'No internet connection' };
  }

  isSyncing = true;
  notifySyncStatus({ syncing: true, progress: 0 });

  try {
    const pendingSales = await getPendingOfflineSales();
    
    if (pendingSales.length === 0) {
      isSyncing = false;
      notifySyncStatus({ syncing: false, progress: 100 });
      return { success: true, synced: 0, message: 'No pending sales to sync' };
    }

    let synced = 0;
    let failed = 0;
    const results = [];
    const failedErrors = [];

    for (let i = 0; i < pendingSales.length; i++) {
      const sale = pendingSales[i];
      const result = await syncSingleSale(sale);
      
      if (result.success) {
        synced++;
      } else {
        failed++;
        failedErrors.push(result.error || 'Unknown error');
      }
      
      results.push({ ...result, offlineId: sale.offline_id });

      const progress = Math.round(((i + 1) / pendingSales.length) * 100);
      notifySyncStatus({ syncing: true, progress, synced, failed, total: pendingSales.length });
      
      if (onProgress) {
        onProgress({ progress, synced, failed, total: pendingSales.length });
      }

      // Small delay between syncs to avoid overwhelming the server
      if (i < pendingSales.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    // Update last sync time
    await setLastSyncTime();

    // Clean up synced sales
    await clearSyncedSales();

    isSyncing = false;
    notifySyncStatus({ syncing: false, progress: 100, synced, failed });

    const failedDetail = failedErrors.length > 0 ? ` Error: ${failedErrors[0]}` : '';
    return {
      success: failed === 0,
      synced,
      failed,
      total: pendingSales.length,
      results,
      message: synced > 0 && failed === 0
        ? `Successfully synced ${synced} sale(s)`
        : failed > 0 && synced > 0
        ? `Synced ${synced}, failed ${failed}.${failedDetail}`
        : `Failed to sync ${failed} sale(s).${failedDetail}`,
    };
  } catch (error) {
    isSyncing = false;
    notifySyncStatus({ syncing: false, error: error.message });
    return { success: false, message: error.message };
  }
};

/**
 * Refresh cached data from server
 */
/**
 * Fetch all rows from a paginated endpoint in chunks.
 * Keeps the server's per-request memory bounded so a single sync can't OOM
 * the production pod, and gives the caller incremental progress callbacks.
 */
const fetchAllPaginated = async (endpoint, { pageSize = 500, onChunk } = {}) => {
  const out = [];
  let skip = 0;
  // Hard upper bound just in case the server stops returning has_more — an
  // infinite loop would be worse than slightly truncated data.
  const SAFETY_CAP = 50000;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await axios.get(`${API}${endpoint}`, {
      params: { limit: pageSize, skip },
    });
    const data = res.data;
    // Endpoint returns either { products|customers|items: [...], has_more, total }
    // or a bare array. Handle both shapes.
    const rows = Array.isArray(data)
      ? data
      : data.products || data.customers || data.items || [];
    out.push(...rows);
    if (onChunk) onChunk({ fetched: out.length, total: data.total ?? null });
    const hasMore = Array.isArray(data) ? rows.length >= pageSize : !!data.has_more;
    if (!hasMore || rows.length === 0 || out.length >= SAFETY_CAP) break;
    skip += pageSize;
  }
  return out;
};

export const refreshOfflineCache = async (onProgress) => {
  if (!navigator.onLine) {
    return { success: false, message: 'No internet connection' };
  }

  try {
    if (onProgress) onProgress({ step: 'products', progress: 0 });

    // Fetch and cache products in chunks via the paginated endpoint so we
    // never ask the server to materialise the full catalogue in one shot.
    // NOTE: backend caps `limit` at 200, so don't ask for more than that.
    const products = await fetchAllPaginated('/api/products/paginated', {
      pageSize: 200,
      onChunk: ({ fetched, total }) => {
        if (onProgress && total) {
          onProgress({ step: 'products', progress: Math.min(50, Math.round((fetched / total) * 50)) });
        }
      },
    });
    await cacheProducts(products);

    if (onProgress) onProgress({ step: 'products', progress: 50 });

    // Fetch and cache customers — /api/customers already supports a `limit`
    // param and a 5000 cap; pull in 500-row pages for parity.
    const customers = await fetchAllPaginated('/api/customers', {
      pageSize: 500,
      onChunk: ({ fetched }) => {
        if (onProgress) onProgress({ step: 'customers', progress: Math.min(95, 50 + Math.round((fetched / 1000) * 5)) });
      },
    });
    await cacheCustomers(customers);

    if (onProgress) onProgress({ step: 'complete', progress: 100 });

    await setLastSyncTime();

    return {
      success: true,
      products: products.length,
      customers: customers.length,
      message: `Cached ${products.length} products and ${customers.length} customers`,
    };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

/**
 * Check if currently syncing
 */
export const isSyncInProgress = () => isSyncing;

/**
 * Auto-sync setup (if enabled)
 */
let autoSyncInterval = null;
let autoCacheInterval = null;
let cacheListeners = [];

/**
 * Register a listener for cache status updates
 */
export const onCacheStatusChange = (callback) => {
  cacheListeners.push(callback);
  return () => {
    cacheListeners = cacheListeners.filter(cb => cb !== callback);
  };
};

/**
 * Notify all listeners of cache status change
 */
const notifyCacheStatus = (status) => {
  cacheListeners.forEach(cb => cb(status));
};

/**
 * Background cache refresh - runs silently in the background.
 *
 * Guards to avoid overwhelming the production DB (was causing the once-an-
 * hour "Failed to load customers" toast at the till):
 *   1. Skip if the tab is CURRENTLY visible AND the cashier interacted in
 *      the last 3 minutes — no point stealing DB throughput mid-transaction.
 *      Offline is what the cache is FOR, and the user has to leave the tab
 *      focused for < 3 min before that becomes relevant.
 *   2. Skip if a successful cache completed less than 45 minutes ago (safety
 *      net against overlapping intervals from multiple tabs).
 *   3. Serialise products then customers with a short breather, and let the
 *      caller pass `force=true` for the manual "Refresh cache" button.
 */
let _lastCacheAt = 0;
let _lastActivityAt = Date.now();
if (typeof window !== 'undefined' && !window.__syncActivityBound) {
  ['mousemove', 'keydown', 'touchstart', 'click', 'pointerdown'].forEach((evt) =>
    window.addEventListener(evt, () => { _lastActivityAt = Date.now(); }, { passive: true, capture: true })
  );
  window.__syncActivityBound = true;
}

export const backgroundCacheRefresh = async ({ force = false } = {}) => {
  if (!navigator.onLine) {
    console.log('Auto-cache: Offline, skipping cache refresh');
    return { success: false, reason: 'offline' };
  }

  if (!force) {
    const now = Date.now();
    const sinceActivity = now - _lastActivityAt;
    const tabVisible = typeof document !== 'undefined' && document.visibilityState === 'visible';
    if (tabVisible && sinceActivity < 3 * 60 * 1000) {
      console.log('Auto-cache: Cashier active — deferring background sync.');
      return { success: false, reason: 'user_active' };
    }
    const sinceLast = now - _lastCacheAt;
    if (_lastCacheAt > 0 && sinceLast < 45 * 60 * 1000) {
      console.log(`Auto-cache: Skipped — last cache only ${Math.round(sinceLast / 60000)} min ago.`);
      return { success: false, reason: 'too_recent' };
    }
  }

  try {
    console.log('Auto-cache: Starting background cache refresh...');
    notifyCacheStatus({ caching: true, type: 'auto' });

    // Use the paginated fetcher so a background refresh can never balloon
    // into a single multi-megabyte response on the production pod.
    // Backend caps `limit` at 200 on this endpoint.
    const products = await fetchAllPaginated('/api/products/paginated', { pageSize: 200 });
    await cacheProducts(products);

    // Small breather between the two heavy pulls so we don't hold the DB
    // pool at max concurrency for the entire sync window.
    await new Promise((r) => setTimeout(r, 500));

    const customers = await fetchAllPaginated('/api/customers', { pageSize: 500 });
    await cacheCustomers(customers);

    await setLastSyncTime();
    _lastCacheAt = Date.now();

    const result = {
      success: true,
      products: products.length,
      customers: customers.length,
      timestamp: new Date().toISOString(),
    };

    console.log(`Auto-cache: Cached ${products.length} products and ${customers.length} customers`);
    notifyCacheStatus({ caching: false, lastCache: result });

    return result;
  } catch (error) {
    console.error('Auto-cache: Error during background refresh:', error.message);
    notifyCacheStatus({ caching: false, error: error.message });
    return { success: false, error: error.message };
  }
};

/**
 * Start automatic background caching.
 * Adds ±15 min of jitter so multiple tabs (or the same user across
 * devices) don't all stampede the API at exactly the top of the hour.
 * @param {number} intervalMs - Cache interval in milliseconds (default: 1 hour)
 */
export const startAutoCache = (intervalMs = 3600000) => {
  // Stop any existing interval
  if (autoCacheInterval) {
    clearInterval(autoCacheInterval);
  }

  const jitterMs = Math.floor((Math.random() - 0.5) * Math.min(intervalMs * 0.5, 30 * 60 * 1000));
  const effective = Math.max(15 * 60 * 1000, intervalMs + jitterMs); // never faster than every 15 min
  console.log(`Auto-cache: Starting with base ${intervalMs / 60000} min + jitter ${Math.round(jitterMs / 60000)} min → next in ${Math.round(effective / 60000)} min`);

  // Delay the FIRST run too — was previously immediate, which meant every
  // tab opened at once (post-deploy) fired a sync simultaneously.
  const firstDelay = Math.random() * Math.min(intervalMs, 15 * 60 * 1000);
  setTimeout(() => backgroundCacheRefresh(), firstDelay);

  // Set up recurring cache with jitter re-rolled every tick.
  autoCacheInterval = setInterval(() => {
    backgroundCacheRefresh();
  }, effective);

  // Store the interval setting
  localStorage.setItem('autoCacheInterval', intervalMs.toString());
  localStorage.setItem('autoCacheEnabled', 'true');

  return true;
};

/**
 * Stop automatic background caching
 */
export const stopAutoCache = () => {
  if (autoCacheInterval) {
    clearInterval(autoCacheInterval);
    autoCacheInterval = null;
  }
  localStorage.setItem('autoCacheEnabled', 'false');
  console.log('Auto-cache: Stopped');
};

/**
 * Check if auto-cache is enabled
 */
export const isAutoCacheEnabled = () => {
  return localStorage.getItem('autoCacheEnabled') === 'true';
};

/**
 * Get auto-cache interval setting
 */
export const getAutoCacheInterval = () => {
  return parseInt(localStorage.getItem('autoCacheInterval') || '3600000', 10);
};

/**
 * Initialize auto-cache on app start if previously enabled
 */
export const initAutoCache = () => {
  if (isAutoCacheEnabled()) {
    const interval = getAutoCacheInterval();
    startAutoCache(interval);
    return true;
  }
  return false;
};

export const startAutoSync = (intervalMs = 60000) => {
  if (autoSyncInterval) {
    clearInterval(autoSyncInterval);
  }

  autoSyncInterval = setInterval(async () => {
    if (navigator.onLine && !isSyncing) {
      const pendingSales = await getPendingOfflineSales();
      if (pendingSales.length > 0) {
        console.log('Auto-sync: Found pending sales, syncing...');
        await syncOfflineSales({ autoMode: true });
      }
    }
  }, intervalMs);

  // Also sync when coming back online
  window.addEventListener('online', async () => {
    console.log('Back online, checking for pending sales...');
    const pendingSales = await getPendingOfflineSales();
    if (pendingSales.length > 0) {
      // AUTO-SYNC when back online
      console.log(`Auto-syncing ${pendingSales.length} pending sales...`);
      notifySyncStatus({ online: true, pendingSales: pendingSales.length, autoSyncing: true });
      
      const result = await syncOfflineSales({ autoMode: true });
      if (result.success && result.synced > 0) {
        notifySyncStatus({ online: true, synced: result.synced, failed: result.failed });
      }
    }
    
    // Also refresh cache when coming back online
    if (isAutoCacheEnabled()) {
      console.log('Back online, refreshing cache...');
      backgroundCacheRefresh();
    }
  });
};

export const stopAutoSync = () => {
  if (autoSyncInterval) {
    clearInterval(autoSyncInterval);
    autoSyncInterval = null;
  }
};

export default {
  syncOfflineSales,
  refreshOfflineCache,
  backgroundCacheRefresh,
  onSyncStatusChange,
  onCacheStatusChange,
  isSyncInProgress,
  startAutoSync,
  stopAutoSync,
  startAutoCache,
  stopAutoCache,
  isAutoCacheEnabled,
  getAutoCacheInterval,
  initAutoCache,
};
